import {
  ApiError,
  NetworkError,
  UnexpectedResponseError,
} from "./errors";
import type { ApiErrorEnvelope } from "./types";

export type ApiRequestOptions = {
  method?: string;
  body?: unknown;
  /**
   * When false, HTTP 401 AUTH_REQUIRED does not invoke the session-expiry handler.
   * Used for bootstrap GET /auth/me.
   */
  handleAuthExpiry?: boolean;
  signal?: AbortSignal;
};

export type ApiClient = {
  request: <T>(path: string, options?: ApiRequestOptions) => Promise<T>;
  setAuthExpiryHandler: (handler: (() => void) | null) => void;
};

export type CreateApiClientOptions = {
  /** Absolute or relative API root. Empty string means same-origin `/api/...`. */
  baseUrl?: string;
  fetchImpl?: typeof fetch;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function parseErrorEnvelope(data: unknown): ApiErrorEnvelope["error"] | null {
  if (!isRecord(data) || !isRecord(data.error)) {
    return null;
  }
  const { code, message, requestId } = data.error;
  if (
    typeof code !== "string" ||
    typeof message !== "string" ||
    typeof requestId !== "string"
  ) {
    return null;
  }
  return { code, message, requestId };
}

/**
 * Central HTTP boundary for the PWA.
 * Always sends credentials so the browser includes the HttpOnly session cookie.
 */
export function createApiClient(
  options: CreateApiClientOptions = {},
): ApiClient {
  const baseUrl = options.baseUrl ?? "";
  const fetchImpl = options.fetchImpl ?? fetch;
  let authExpiryHandler: (() => void) | null = null;

  async function request<T>(
    path: string,
    requestOptions: ApiRequestOptions = {},
  ): Promise<T> {
    const method = requestOptions.method ?? "GET";
    const handleAuthExpiry = requestOptions.handleAuthExpiry !== false;
    const headers: Record<string, string> = {
      Accept: "application/json",
    };

    let body: BodyInit | undefined;
    if (requestOptions.body instanceof FormData) {
      body = requestOptions.body;
    } else if (requestOptions.body !== undefined) {
      headers["Content-Type"] = "application/json";
      body = JSON.stringify(requestOptions.body);
    }

    let response: Response;
    try {
      response = await fetchImpl(`${baseUrl}${path}`, {
        method,
        headers,
        body,
        credentials: "include",
        signal: requestOptions.signal,
      });
    } catch {
      throw new NetworkError();
    }

    if (response.status === 204) {
      return undefined as T;
    }

    const contentType = response.headers.get("content-type") ?? "";
    const isJson = contentType.includes("application/json");

    if (!isJson) {
      if (response.ok) {
        throw new UnexpectedResponseError(
          `Expected JSON response, got ${contentType || "unknown content type"}`,
        );
      }
      const text = await response.text().catch(() => "");
      throw new UnexpectedResponseError(
        `Unexpected error response (${response.status})${text ? `: ${text.slice(0, 120)}` : ""}`,
      );
    }

    let data: unknown;
    try {
      data = await response.json();
    } catch {
      throw new UnexpectedResponseError(
        `Failed to parse JSON response (${response.status})`,
      );
    }

    if (!response.ok) {
      const envelope = parseErrorEnvelope(data);
      if (!envelope) {
        throw new UnexpectedResponseError(
          `Malformed error envelope (${response.status})`,
        );
      }

      if (
        handleAuthExpiry &&
        response.status === 401 &&
        envelope.code === "AUTH_REQUIRED"
      ) {
        authExpiryHandler?.();
      }

      throw new ApiError(
        envelope.code,
        envelope.message,
        response.status,
        envelope.requestId,
      );
    }

    return data as T;
  }

  return {
    request,
    setAuthExpiryHandler(handler) {
      authExpiryHandler = handler;
    },
  };
}

/** Shared singleton for the running app. Tests create their own clients. */
export const apiClient = createApiClient();
